import { useState } from 'react';
import { useAccountApi } from '../../api/AccountApiProvider';
import { toApiError } from '../../api/errors';
import type { ApiError } from '../../api/errors';
import type { Rollout, RolloutBoard } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { AttributionNotice } from '../../ui/AttributionNotice';
import { Button, Chip } from '../../ui/primitives';
import { Modal } from '../../ui/Modal';
import { retryOffer, waveLabelKey } from './rollout';

/**
 * Moving the tenancies of this wave that did not move, once somebody has put
 * them right.
 *
 * **This is the way out of a wave one tenancy failed in.** A failed move is a
 * finding and a finding holds the wave — deliberately, because that is what
 * makes a canary a canary. Repairing the tenancy alone changes nothing on the
 * board: the step belongs to the rollout that wrote it, so without this the
 * wave stayed unsignable however healthy the tenancy became.
 *
 * **It is not a way past the wave**, and the dialog says so rather than leaving
 * somebody to assume otherwise: only the tenancies that did not move are moved,
 * the wave still has to be signed off afterwards, and the wave after it still
 * waits for that sign-off. The order of the waves is untouched.
 *
 * Nothing here chooses who moves. The list is the server's — a list in the
 * request would mean a wave could be signed off by declaring part of it not to
 * count — and this dialog shows it so that pressing is done with the names in
 * view.
 */
export function RetryMovesDialog({
  rollout,
  actorName,
  onClose,
  onRetried,
}: {
  rollout: Rollout;
  actorName: string;
  onClose: () => void;
  onRetried: (board: RolloutBoard) => void;
}) {
  const { t } = useI18n();
  const api = useAccountApi();

  const [failure, setFailure] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const offer = retryOffer(rollout);

  async function retry(): Promise<void> {
    setFailure(null);
    setBusy(true);
    try {
      onRetried(await api.provider.retryRollout());
    } catch (error: unknown) {
      setFailure(toApiError(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('rolloutRetry.title')}
      subtitle={t('rolloutRetry.subtitle', {
        wave: t(waveLabelKey(rollout.wave)),
        target: rollout.target,
      })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <span className="spacer" />
          <Button variant="primary" disabled={busy || !offer.offered} onClick={() => void retry()}>
            {busy ? `${t('rolloutRetry.submitting')}…` : t('rolloutRetry.submit')}
          </Button>
        </>
      }
    >
      <div className="stack">
        {failure !== null && <RetryFailure failure={failure} />}

        {offer.offered ? (
          <Alert tone="warn" title={t('rolloutRetry.willMove.title')}>
            <p>{t('rolloutRetry.willMove.body', { target: rollout.target })}</p>
            <div className="row" style={{ marginTop: 8 }}>
              {offer.tenants.map((slug) => (
                <Chip key={slug}>{slug}</Chip>
              ))}
            </div>
          </Alert>
        ) : (
          // The board carries this sentence too; it is repeated here because
          // this is the screen where somebody was about to press the button.
          <Alert tone="neutral" title={t('rollout.retry.blocked.title')} live>
            {offer.blockedBy === undefined ? (
              <p>{t('rollout.retry.blocked.noReason')}</p>
            ) : (
              <p className="mono">{offer.blockedBy}</p>
            )}
          </Alert>
        )}

        <Alert tone="neutral" title={t('rolloutRetry.notAWayPast.title')}>
          <p>{t('rolloutRetry.notAWayPast.body')}</p>
        </Alert>

        <AttributionNotice actorName={actorName} />
      </div>
    </Modal>
  );
}

/**
 * A refusal of the retry itself.
 *
 * `conflict` is the server saying which of its four it is — still moving,
 * stopped, over, or nothing left that did not move — and the sentence is shown
 * as it stands. The last of the four is the one worth reading twice: it says
 * whatever is still holding the wave is about a tenancy rather than about a
 * move, and is put right on that tenancy's own card.
 */
function RetryFailure({ failure }: { failure: ApiError }) {
  const { t } = useI18n();

  if (failure.code === 'conflict') {
    return (
      <Alert tone="deny" title={t('rolloutRetry.refused.title')} live>
        <p className="mono">{failure.message}</p>
        <p style={{ marginTop: 8 }}>{t('rolloutRetry.refused.body')}</p>
      </Alert>
    );
  }
  return <ErrorAlert error={failure} />;
}
